import Classification from 'models/classification/classification';
import CurrentUser from 'models/currentuser';
import UserLanguage from 'models/userLanguage';
import { apiGetClassifications } from 'services/Api/classificationService';
import { apiRequest } from 'services/Auth/authConfig';
import { EntityCache, ICache } from './entityCache';

export class ClassificationCache
  extends EntityCache<Classification>
  implements ICache<Classification, ClassificationCache>
{
  private isLoading: boolean = false;

  private classificationsPromise: Promise<Classification[]> | undefined;

  private currentUser?: CurrentUser;

  constructor() {
    super();
    this.getId = (classification) => classification.classificationId;
    this.getEmptyItem = () => new Classification();
  }

  setCurrentUserLanguage(user: CurrentUser) {
    this.currentUser = user;
    this.items.forEach((classification) => this.setClassificationLanguage(classification));
  }

  async getItems(refresh: boolean = false): Promise<Classification[]> {
    if (this.isLoading && this.classificationsPromise) {
      return await this.classificationsPromise;
    }

    if (this.items.length === 0 || refresh) {
      if (this.appContext && !this.isLoading) {
        try {
          const accessToken = await this.appContext.getAccessToken(apiRequest.scopes);
          this.classificationsPromise = apiGetClassifications(accessToken, this.appContext.globalDataCache, {
            includeAllLanguages: true,
          });
          this.isLoading = true;
          this.items = await this.classificationsPromise;
          this.items.forEach((classification) => this.setClassificationLanguage(classification));
        } catch (err) {
          this.appContext.setError(err);
        } finally {
          this.isLoading = false;
          this.classificationsPromise = undefined;
        }
      } else if (!this.appContext) {
        this.items = [];
      }
    }

    if (this.isLoading && this.classificationsPromise) {
      return await this.classificationsPromise;
    }

    return this.items;
  }

  override add(item: Classification) {
    this.setClassificationLanguage(item);
    super.add(item);
  }

  override update(item: Classification) {
    this.setClassificationLanguage(item);
    super.update(item);
  }

  clone(): ClassificationCache {
    const classificationCache = new ClassificationCache();
    classificationCache.items = this.items?.map((classification) => classification.clone());
    classificationCache.currentUser = this.currentUser;

    return classificationCache;
  }

  private setClassificationLanguage(classification: Classification) {
    const languageCode = this.currentUser?.language.code;
    if (!languageCode) return;

    classification.transIdx = this.getTransIdx(classification.trans, languageCode);
    if (classification.transIdx >= 0) {
      classification.name = classification.trans[classification.transIdx].name;
      classification.description = classification.trans[classification.transIdx].description;
      classification.examples = classification.trans[classification.transIdx].examples;
    }

    classification.group.transIdx = this.getTransIdx(classification.group.trans, languageCode);
    if (classification.group.transIdx >= 0) {
      classification.group.value = classification.group.trans[classification.group.transIdx].value;
      classification.group.description = classification.group.trans[classification.group.transIdx].description;
    }
  }

  private getTransIdx(translations: { languageId: number }[], languageCode: string): number {
    const languageId = UserLanguage.getLanguageId(languageCode);
    const fallbackLanguageId = UserLanguage.getLanguageId(UserLanguage.getFallBack());
    let transIdx = translations.findIndex((translation) => translation.languageId === languageId);
    if (transIdx === -1) {
      transIdx = translations.findIndex((translation) => translation.languageId === fallbackLanguageId);
    }
    if (transIdx === -1 && translations.length > 0) {
      transIdx = 0;
    }

    return transIdx;
  }
}
